import Alert from '../../../common/components/Alert';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import Grid from '@material-ui/core/Grid';
import InputLabel from '@material-ui/core/InputLabel';
import Link from '@material-ui/core/Link';
import React from 'react';
import ReactHtmlParser from 'react-html-parser';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import { AuthPageStyles } from '../../styles';
import { IELink } from '../../../common/components/IELink';
import { Link as RouterLink } from 'react-router-dom';
import { MfsLoginFields } from '../../interfaces/AuthInputFields';
import { PasswordStrengthMessages } from '../PasswordStrengthMessages';
import { PasswordStrengthMeter } from '../PasswordStrengthMeter';
import { routeMappings } from '../../../constants';
import { useTranslation } from 'react-i18next';

interface MfsPasswordExpiredFormProps {
  formFields: MfsLoginFields;
  errorMessage: string;
  loadingOnLogin: boolean;
  onLoginClick(event: React.SyntheticEvent): Promise<void>;
  onInputChange(name: keyof MfsLoginFields): (event: React.ChangeEvent<{ value: string }>) => void;
}

/** SignUp Component for MFS */
export const MfsPasswordExpiredForm = ({
  formFields,
  errorMessage,
  loadingOnLogin,
  onLoginClick,
  onInputChange,
}: MfsPasswordExpiredFormProps): JSX.Element => {
  const { t } = useTranslation();
  const classes = AuthPageStyles();

  return (
    <form onSubmit={onLoginClick} aria-label={t('passwordExpired')}>
      <Box mb={3}>
        <Typography variant="h2">{t('passwordExpired')}</Typography>
      </Box>

      <Box mt={3}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                {/** Current Password */}
                <Grid container spacing={1}>
                  <Grid item xs={12}>
                    <InputLabel htmlFor="currentPassword">
                      <b>{t('currentPassword')}</b>
                    </InputLabel>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      id="currentPassword"
                      type="password"
                      variant="outlined"
                      required
                      fullWidth
                      name="password"
                      size="small"
                      value={formFields.password}
                      error={formFields.passwordHelperText !== ''}
                      helperText={formFields.passwordHelperText !== '' && t(formFields.passwordHelperText)}
                      placeholder={t('currentPasswordPlaceholder')}
                      onChange={onInputChange('password')}
                      onBlur={onInputChange('password')}
                      inputProps={{
                        'aria-label': 'current Password',
                        maxLength: '50',
                      }}
                    />
                  </Grid>
                </Grid>

                <Grid item xs={12}>
                  <Box pt={1}>
                    <Link component={RouterLink} to={`${routeMappings['MFS']}${routeMappings['FORGOT_PASSWORD']}`}>
                      <IELink>{`${t('forgottenPassword')}?`}</IELink>
                    </Link>
                  </Box>
                </Grid>
              </Grid>

              <Grid item xs={12} sm={6}>
                {/** New Password */}
                <Grid container spacing={1}>
                  <Grid item xs={12}>
                    <InputLabel htmlFor="newPassword">
                      <b>{t('newPassword')}</b>
                    </InputLabel>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      id="newPassword"
                      type="password"
                      variant="outlined"
                      required
                      fullWidth
                      name="newPassword"
                      placeholder={t('setANewPasswordPlaceholder')}
                      size="small"
                      value={formFields.newPassword}
                      error={formFields.newPasswordHelperText !== ''}
                      helperText={formFields.newPasswordHelperText !== '' && t(formFields.newPasswordHelperText)}
                      onChange={onInputChange('newPassword')}
                      onBlur={onInputChange('newPassword')}
                      inputProps={{
                        'aria-label': 'newPassword',
                        maxLength: '50',
                      }}
                    />
                  </Grid>
                </Grid>
              </Grid>

              <Grid item xs={12} sm={6}>
                {/** Confirm New Password */}
                <Grid container spacing={1}>
                  <Grid item xs={12}>
                    <InputLabel htmlFor="confirmNewPassword">
                      <b>{t('confirmNewPassword')}</b>
                    </InputLabel>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      id="confirmNewPassword"
                      type="password"
                      variant="outlined"
                      required
                      fullWidth
                      name="confirmNewPassword"
                      placeholder={t('confirmNewPasswordPlaceholder')}
                      size="small"
                      value={formFields.confirmNewPassword}
                      error={formFields.confirmNewPasswordHelperText !== ''}
                      helperText={
                        formFields.confirmNewPasswordHelperText !== '' && t(formFields.confirmNewPasswordHelperText)
                      }
                      onChange={onInputChange('confirmNewPassword')}
                      onBlur={onInputChange('confirmNewPassword')}
                      inputProps={{
                        'aria-label': 'confirmNewPassword',
                        maxLength: '50',
                      }}
                    />
                  </Grid>
                </Grid>
              </Grid>

              {/** passowrd notes */}
              <PasswordStrengthMessages
                email={formFields.email}
                password={formFields.newPassword}
                className={classes.progressMeterAlert}
              />

              {/** Strength Indicator */}
              <Grid item xs={12} sm={6}>
                <Grid container spacing={1} alignItems="center">
                  <Grid item xs={12}>
                    <InputLabel htmlFor="strength">
                      <b>{t('passwordStrength')}</b>
                    </InputLabel>
                  </Grid>
                  <Grid item xs={12}>
                    <PasswordStrengthMeter password={formFields.newPassword} />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          {/** alert message in case sign up api fails */}
          <Alert show={errorMessage !== ''} state="error">
            {ReactHtmlParser(errorMessage, { decodeEntities: true })}
          </Alert>

          {/** button for sign in */}
          <Grid item xs={12}>
            <Box width="100%" textAlign="center">
              <Button
                type="submit"
                variant="outlined"
                color="primary"
                disabled={loadingOnLogin}
                aria-hidden={loadingOnLogin}
                aria-disabled="false"
                onClick={onLoginClick}>
                <div>{t('changePasswordAndSignIn')}</div>
                {loadingOnLogin && <CircularProgress size={25} className={classes.buttonProgress} />}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </form>
  );
};
